import { createRoot, type Root } from 'react-dom/client';
import { StrictMode } from 'react';
import './design/tokens.css';
import { Providers } from './app/providers';
import { LimiteDeErrores } from './components/LimiteDeErrores';
import { Sistema } from './features/sistema/Sistema';
import { Perfil } from './features/perfil/Perfil';
import { Solicitudes } from './features/solicitudes/Solicitudes';
import { Inicio } from './features/inicio/Inicio';
import { Novedades } from './features/novedades/Novedades';
import { Reconocimientos } from './features/reconocimientos/Reconocimientos';
import { Celebraciones } from './features/celebraciones/Celebraciones';
import { Directorio } from './features/directorio/Directorio';
import { Comunicaciones } from './features/comunicaciones/Comunicaciones';
import { MensajesDirectos } from './features/notificaciones/MensajesDirectos';
import { Biblioteca } from './features/biblioteca/Biblioteca';
import { Eventos } from './features/eventos/Eventos';
import { Calendario } from './features/eventos/Calendario';
import { Galeria } from './features/eventos/Galeria';
import { Comunidades } from './features/comunidades/Comunidades';
import { Cursos } from './features/formacion/Cursos';
import { DetalleDeCurso } from './features/formacion/DetalleDeCurso';
import { Rutas } from './features/formacion/rutas/Rutas';
import { Competencias } from './features/formacion/competencias/Competencias';
import { Induccion } from './features/formacion/induccion/Induccion';
import { Encuestas } from './features/encuestas/Encuestas';
import { Informes } from './features/informes/Informes';
import { Analitica } from './features/analitica/Analitica';
import { RecursosYDatos } from './features/recursos/RecursosYDatos';
import { Documentos } from './features/documentos/Documentos';
import { MisDocumentos } from './features/documentos/MisDocumentos';
import { Administracion } from './features/administracion/Administracion';
import { Auditoria } from './features/administracion/Auditoria';
import { FormularioPublico } from './features/publico/FormularioPublico';
import { Acceso } from './features/acceso/Acceso';
import { Cromo } from './features/armazon/Cromo';

/**
 * main.tsx — el puente entre el shell vanilla y la capa React.
 *
 * No es una aplicación de React al uso: no se apodera del documento. Se compila
 * como un `<script>` más de los que ya carga `index.html`, y expone las
 * funciones que llama el router de `app.js` cuando toca una pantalla migrada.
 *
 * Por qué así, y no migrando el shell primero: es lo único que permite migrar
 * **una pantalla cada vez**, desplegar cada una por separado y retroceder
 * quitando una línea del PAGE_MAP. El shell —barra lateral, cabecera, router,
 * sesión— se migra al final, cuando ya no quede ninguna pantalla vanilla que
 * dependa de él.
 */

/** Las pantallas migradas. Añadir una es una línea aquí y una en el PAGE_MAP. */
const PANTALLAS = {
  sistema: Sistema,
  perfil: Perfil,
  solicitudes: Solicitudes,
  inicio: Inicio,
  novedades: Novedades,
  reconocimientos: Reconocimientos,
  celebraciones: Celebraciones,
  directorio: Directorio,
  comunicaciones: Comunicaciones,
  notificaciones: MensajesDirectos,
  biblioteca: Biblioteca,
  eventos: Eventos,
  calendario: Calendario,
  'galeria-evento': Galeria,
  comunidades: Comunidades,
  cursos: Cursos,
  'detalle-curso': DetalleDeCurso,
  rutas: Rutas,
  competencias: Competencias,
  induccion: Induccion,
  encuestas: Encuestas,
  informes: Informes,
  analitica: Analitica,
  'recursos-y-datos': RecursosYDatos,
  documentos: Documentos,
  'mis-documentos': MisDocumentos,
  administracion: Administracion,
  // Auditoría tiene su propia pantalla en el menú y además es una sección de
  // Administración. Es el MISMO componente: eran dos tablas del mismo endpoint,
  // cada una con la mitad de las columnas y solo una con filtro.
  auditoria: () => <Auditoria suelta />,
  // La primera pantalla SIN sesión de la capa React. No está en el `PAGE_MAP`
  // —el router solo reparte lo que hay detrás del login— y la monta `app.js`
  // desde `showPublicForm()`, que es quien decide qué pantalla de entrada se ve.
  'formulario-publico': FormularioPublico,
  // Las tres pantallas de acceso. Son UN componente con tres modos —no son tres
  // destinos, son tres momentos de lo mismo— y tres claves porque `app.js`
  // sigue decidiendo cuál se ve: `showLogin()` es contrato de `auth.js` y de
  // `api.js`, que lo llaman al cerrar sesión y ante un 401.
  acceso: () => <Acceso modo="entrar" />,
  'acceso-olvide': () => <Acceso modo="olvide" />,
  'acceso-restablecer': () => <Acceso modo="restablecer" />,
} satisfies Record<string, React.ComponentType>;

export type ClavePantalla = keyof typeof PANTALLAS;

let raiz: Root | null = null;

function montar(clave: string, contenedor: HTMLElement) {
  const Pantalla = PANTALLAS[clave as ClavePantalla];
  if (!Pantalla) {
    // Falla ruidosamente y a propósito: un PAGE_MAP que apunta a una pantalla
    // que no existe es un error de programación, y devolver una pantalla en
    // blanco lo escondería hasta que alguien lo notara en producción.
    throw new Error(`[PortalReact] No hay ninguna pantalla registrada como "${clave}"`);
  }
  desmontar();
  raiz = createRoot(contenedor);
  // Sin el límite, un fallo al pintar deja el contenedor vacío y sin mensaje.
  // Por fuera de `Providers` a propósito: ver `components/LimiteDeErrores.tsx`.
  raiz.render(
    <StrictMode>
      <LimiteDeErrores pantalla={clave}>
        <Providers>
          <Pantalla />
        </Providers>
      </LimiteDeErrores>
    </StrictMode>,
  );
}

/* ── El cromo: la segunda raíz ─────────────────────────────────────────
 *
 * Menú lateral, cabecera, búsqueda, campana, aviso de ficha y barra inferior:
 * todo lo que rodea a la pantalla. Es una raíz APARTE de la de las pantallas, y
 * eso es lo que permite migrar el armazón sin migrar el router — `app.js` sigue
 * repartiendo y montando cada pantalla en `#page-container`.
 *
 * No se desmonta al navegar. Sí al salir: ver la cabecera de `Cromo.tsx`, que
 * cuenta el bucle de 401 que provocaría dejarlo vivo detrás del acceso.
 */

let raizCromo: Root | null = null;

function montarCromo(contenedor: HTMLElement) {
  if (raizCromo) return;          // se monta una vez por sesión
  raizCromo = createRoot(contenedor);
  raizCromo.render(
    <StrictMode>
      <LimiteDeErrores pantalla="armazón">
        <Providers>
          <Cromo />
        </Providers>
      </LimiteDeErrores>
    </StrictMode>,
  );
}

function desmontarCromo() {
  if (!raizCromo) return;
  raizCromo.unmount();
  raizCromo = null;
}

function desmontar() {
  if (!raiz) return;
  // `unmount()` síncrono dentro del ciclo de render de React avisa por consola.
  // Aquí siempre se llama desde el router —fuera de cualquier render—, así que
  // es seguro y evita que queden suscripciones vivas de la pantalla anterior.
  raiz.unmount();
  raiz = null;
}

/* ── Las raíces incrustadas se cerraron el 2026-09-18 ──────────────────
 *
 * Aquí vivían `montarPublicaciones` y `desmontarPublicaciones`: un árbol de
 * React montado DENTRO del HTML de una pantalla que todavía era vanilla. Existía
 * por un solo caso —Comunidades pintaba su muro con la tarjeta de Novedades— y
 * el comentario que estaba aquí ya decía que se cerraría al migrarla (módulo 9).
 *
 * Migrada. La pantalla usa `Publicacion` directamente, y con la puerta se fueron
 * `features/novedades/MuroIncrustado.tsx` y la clase `.feed-composer`.
 *
 * **El portal ya no tiene ninguna raíz React incrustada**, y esa es la noticia:
 * cada pantalla es entera de un lado o del otro. Si vuelve a hacer falta una,
 * que sea con la misma pregunta de entonces —¿o dos copias del mismo
 * componente?— y con fecha de caducidad escrita.
 *
 * El cromo, que es la segunda raíz desde el 2026-09-28, NO es una de estas. Una
 * raíz incrustada es un trozo de React DENTRO del HTML que pinta una pantalla
 * vanilla: el vanilla es el dueño del contenedor y lo repinta con `innerHTML`
 * cuando quiere, dejando la raíz apuntando a nodos que ya no están. El cromo es
 * el dueño de los suyos y nadie más los toca.
 */

declare global {
  interface Window {
    PortalReact?: {
      montar: (clave: string, contenedor: HTMLElement) => void;
      desmontar: () => void;
      montarCromo: (contenedor: HTMLElement) => void;
      desmontarCromo: () => void;
      pantallas: string[];
    };
  }
}

window.PortalReact = {
  montar,
  desmontar,
  montarCromo,
  desmontarCromo,
  pantallas: Object.keys(PANTALLAS),
};
